/**
 * Gestionnaire d'authentification automatique pour omnia-eg
 * Gère automatiquement les tokens, leur nettoyage et leur renouvellement
 * MODIFIÉ pour utiliser des données factices
 */

import { buildApiUrl } from '../utils/apiUrl';

export interface AuthTokens {
  access: string;
  refresh: string;
}

export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role?: string;
  roles?: string[];
  capabilities?: string[];
  company?: string;
  department?: string;
  department_id?: number | null;
  department_name?: string | null;
  position?: string;
}

class AuthManager {
  private static instance: AuthManager;
  private tokenRefreshTimeout: NodeJS.Timeout | null = null;
  private renouvellementEnCours: Promise<boolean> | null = null;
  private readonly TOKEN_KEY = 'access_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly USER_KEY = 'user';
  private readonly TOKEN_EXPIRY_KEY = 'token_expiry';

  private constructor() {
    this.initializeAuth();
  }

  public static getInstance(): AuthManager {
    if (!AuthManager.instance) {
      AuthManager.instance = new AuthManager();
    }
    return AuthManager.instance;
  }

  /**
   * Initialise l'authentification et nettoie les anciens tokens
   */
  private initializeAuth(): void {
    console.log('🔧 Initialisation du gestionnaire d\'authentification (Mode Données Factices)...');
    
    // Nettoyer les anciens tokens expirés
    this.cleanupExpiredTokens();
    
    // Vérifier et renouveler le token si nécessaire
    this.checkAndRefreshToken();
    
    // Configurer le renouvellement automatique
    this.setupAutoRefresh();
  }

  /**
   * Nettoie les tokens expirés du localStorage
   */
  private cleanupExpiredTokens(): void {
    const token = localStorage.getItem(this.TOKEN_KEY);
    const expiry = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
    
    if (token && expiry) {
      const expiryTime = parseInt(expiry);
      const now = Date.now();
      
      if (now >= expiryTime) {
        console.log('🗑️ Token expiré détecté, nettoyage...');
        this.clearAuth();
        return;
      }
      
      // Vérifier si le token expire dans les 5 prochaines minutes
      const fiveMinutesFromNow = now + (5 * 60 * 1000);
      if (expiryTime <= fiveMinutesFromNow) {
        console.log('⚠️ Token expire bientôt, renouvellement nécessaire...');
        this.refreshToken();
      }
    }
  }

  /**
   * Vérifie et renouvelle le token si nécessaire
   */
  private async checkAndRefreshToken(): Promise<void> {
    const token = localStorage.getItem(this.TOKEN_KEY);
    const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);
    
    if (!token || !refreshToken) {
      console.log('🔍 Aucun token trouvé');
      return;
    }

    try {
      // En mode données factices, on simule la validation
      const isValid = await this.validateToken(token);
      
      if (!isValid) {
        console.log('🔄 Token invalide, tentative de renouvellement...');
        await this.refreshToken();
      }
    } catch (error) {
      console.error('❌ Erreur lors de la validation du token:', error);
      this.clearAuth();
    }
  }

  /**
   * Valide un token JWT réel
   */
  private async validateToken(token: string): Promise<boolean> {
    try {
      // Décoder le token JWT pour vérifier l'expiration
      const payload = JSON.parse(atob(token.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);
      return payload.exp > now;
    } catch (error) {
      console.error('❌ Erreur de validation du token:', error);
      return false;
    }
  }

  /**
   * Renouvelle le token d'accès via l'API
   */
  private async refreshToken(): Promise<void> {
    const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);
    
    if (!refreshToken) {
      console.log('❌ Aucun refresh token disponible');
      this.clearAuth();
      return;
    }

    try {
      // Appel API pour renouveler le token
      // BEST PRACTICE: Utiliser buildApiUrl() pour construire l'URL de manière sûre
      const response = await fetch(buildApiUrl('/users/auth/refresh-token/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh: refreshToken }),
      });

      if (!response.ok) {
        throw new Error('Erreur de renouvellement du token');
      }

      const data = await response.json();
      this.setTokens(data.access, data.refresh || refreshToken);
      console.log('✅ Token renouvelé avec succès');
    } catch (error) {
      console.error('❌ Erreur lors du renouvellement:', error);
      this.clearAuth();
    }
  }

  /**
   * Configure le renouvellement automatique des tokens
   */
  private setupAutoRefresh(): void {
    // Vérifier toutes les 4 minutes (tokens JWT expirent généralement après 5 minutes)
    const REFRESH_INTERVAL = 4 * 60 * 1000; // 4 minutes
    
    this.tokenRefreshTimeout = setInterval(() => {
      this.checkAndRefreshToken();
    }, REFRESH_INTERVAL);
  }

  /**
   * Stocke les tokens et configure leur expiration
   */
  public setTokens(accessToken: string, refreshToken: string): void {
    // Décoder le token JWT pour obtenir l'expiration
    const expiry = this.getTokenExpiry(accessToken);
    
    localStorage.setItem(this.TOKEN_KEY, accessToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
    localStorage.setItem(this.TOKEN_EXPIRY_KEY, expiry.toString());
    
    console.log('💾 Tokens stockés avec expiration:', new Date(expiry));
  }

  /**
   * Stocke les informations utilisateur
   */
  public setUser(user: User): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    console.log('👤 Utilisateur stocké:', user.full_name);
  }

  /**
   * Récupère le token d'accès
   */
  public getAccessToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Récupère le refresh token
   */
  public getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  /**
   * Récupère les informations utilisateur
   */
  public getUser(): User | null {
    const userStr = localStorage.getItem(this.USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  }

  /**
   * Retourne les codes de rôle de l'utilisateur courant
   */
  public getRoles(): string[] {
    const user = this.getUser();
    if (!user) return [];
    
    // Normaliser tous les rôles en minuscules
    const roles: string[] = [];
    
    // Ajouter les rôles du tableau roles
    if (Array.isArray(user.roles) && user.roles.length > 0) {
      user.roles.forEach((r: string) => {
        const normalized = String(r || '').toLowerCase().trim();
        if (normalized && !roles.includes(normalized)) {
          roles.push(normalized);
        }
      });
    }
    
    // Ajouter le rôle principal (champ role)
    if (user.role) {
      const normalized = String(user.role || '').toLowerCase().trim();
      if (normalized && !roles.includes(normalized)) {
        roles.push(normalized);
      }
    }
    
    return roles;
  }

  public hasRole(roleCode: string): boolean {
    const roles = this.getRoles();
    const normalizedRoleCode = String(roleCode || '').toLowerCase().trim();
    return roles.some((r: string) => String(r || '').toLowerCase().trim() === normalizedRoleCode);
  }

  public hasAnyRole(roleCodes: string[]): boolean {
    const roles = this.getRoles();
    return roleCodes.some(code => roles.includes(code));
  }

  public getCapabilities(): string[] {
    const user = this.getUser();
    if (!user) return [];
    return Array.isArray(user.capabilities) ? user.capabilities : [];
  }

  public hasCapability(capabilityCode: string): boolean {
    // Si l'utilisateur est admin, il a toutes les capabilities
    if (this.hasRole('admin')) {
      return true;
    }
    const capabilities = this.getCapabilities();
    return capabilities.includes(capabilityCode);
  }

  public hasAnyCapability(capabilityCodes: string[]): boolean {
    // Si l'utilisateur est admin, il a toutes les capabilities
    if (this.hasRole('admin')) {
      return true;
    }
    const capabilities = this.getCapabilities();
    return capabilityCodes.some(code => capabilities.includes(code));
  }

  public hasAllCapabilities(capabilityCodes: string[]): boolean {
    // Si l'utilisateur est admin, il a toutes les capabilities
    if (this.hasRole('admin')) {
      return true;
    }
    const capabilities = this.getCapabilities();
    return capabilityCodes.every(code => capabilities.includes(code));
  }

  /**
   * Vérifie si l'utilisateur est connecté
   */
  public isAuthenticated(): boolean {
    const token = this.getAccessToken();
    const user = this.getUser();
    return !!(token && user);
  }

  /**
   * Renouvelle le token à la demande, et dit si l'appel peut être rejoué.
   *
   * Le renouvellement périodique ne suffit pas : entre deux passages, une mise
   * en veille de la machine ou un onglet laissé de côté suffisent à laisser
   * expirer le token. La requête part alors avec un token périmé et le serveur
   * la refuse. Plutôt que de remonter l'erreur à l'écran, on renouvelle au
   * moment du refus et on rejoue.
   *
   * Les renouvellements simultanés partagent le même appel : deux requêtes
   * refusées en même temps consommeraient sinon le même refresh token, et la
   * seconde échouerait puisqu'il aura tourné.
   */
  public async renouvelerJeton(): Promise<boolean> {
    if (!this.renouvellementEnCours) {
      this.renouvellementEnCours = this.refreshToken()
        .then(() => !!this.getAccessToken())
        .catch(() => false)
        .finally(() => {
          this.renouvellementEnCours = null;
        });
    }
    return this.renouvellementEnCours;
  }

  /**
   * Nettoie toutes les données d'authentification
   */
  public clearAuth(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
    
    if (this.tokenRefreshTimeout) {
      clearInterval(this.tokenRefreshTimeout);
      this.tokenRefreshTimeout = null;
    }
    
    console.log('🗑️ Authentification nettoyée');
  }

  /**
   * Extrait l'expiration d'un token JWT
   */
  private getTokenExpiry(token: string): number {
    try {
      // Décoder le token JWT pour obtenir l'expiration
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000; // Convertir en millisecondes
    } catch (error) {
      console.error('❌ Erreur de décodage du token:', error);
      // Si on ne peut pas décoder le token, on suppose qu'il expire dans 1 heure
      return Date.now() + (60 * 60 * 1000);
    }
  }

}

// Export de l'instance singleton
export const authManager = AuthManager.getInstance();

// Fonction utilitaire pour nettoyer automatiquement au chargement de la page
export const initializeAuth = (): void => {
  // Nettoyer automatiquement les tokens expirés au chargement
  // L'instance est déjà créée par le singleton
};

// Initialiser automatiquement au chargement du module
initializeAuth();
